/**
 * WorkshopDayDrawer — the BON-142 day-detail drawer for the booking picker's
 * dense calendar: opens from a day cell's "+N more" link or count badge,
 * listing EVERY workshop on that day (not just the ones hidden by the cap) as
 * a selectable row. Replaces the shared calendar's built-in
 * `CalendarDayOverflow` popover for THIS call-site only — the rebook and
 * dashboards calendars keep that popover (see `WorkshopCalendarPicker`'s
 * `dense` prop, left off there).
 *
 * Selecting a row is a DIRECT pick (unlike the calendar's select-then-confirm
 * footer flow): it closes the drawer and hands the workshop straight back to
 * the "New booking" form. Slides in from the right edge of the "Choose a
 * workshop" dialog (not the whole viewport) over a light scrim; Escape,
 * outside-click, or the × close it without picking. A lightweight focus trap
 * keeps Tab cycling inside while it's open.
 */
import { format } from 'date-fns';
import { useEffect, useId, useRef } from 'react';

import { useFocusTrap } from '../../../hooks/useFocusTrap';

import type { PickerWorkshop } from './pickerWorkshop';
import { WorkshopDayDrawerHeader } from './WorkshopDayDrawerHeader';
import { WorkshopDayDrawerRow } from './WorkshopDayDrawerRow';

export interface WorkshopDayDrawerProps {
  day: Date;
  /** Every workshop on `day` (already region/tree-species filtered upstream). */
  workshops: PickerWorkshop[];
  /** "all regions" or the active region's name — feeds the subline. */
  regionScopeLabel: string;
  selectedId: string | null;
  /** A row was clicked — the caller picks it AND closes the drawer. */
  onSelect: (id: string) => void;
  /** ×, Escape, or outside-click — closes WITHOUT picking. */
  onClose: () => void;
}

export function WorkshopDayDrawer({
  day,
  workshops,
  regionScopeLabel,
  selectedId,
  onSelect,
  onClose,
}: WorkshopDayDrawerProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { autoFocusFirst: true });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="absolute inset-0 z-[70]" role="presentation" onClick={onClose}>
      <div className="absolute inset-0 bg-stone-900/10 dark:bg-black/30" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-stone-200 bg-white shadow-2xl dark:border-stone-700 dark:bg-[#1a1f1c]"
      >
        <WorkshopDayDrawerHeader
          day={day}
          count={workshops.length}
          regionScopeLabel={regionScopeLabel}
          titleId={titleId}
          onClose={onClose}
        />
        <div
          role="listbox"
          aria-label={`Workshops on ${format(day, 'MMMM d')}`}
          className="flex-1 min-h-0 space-y-2 overflow-y-auto px-4 py-4"
        >
          {workshops.map((w) => (
            <WorkshopDayDrawerRow
              key={w.id}
              workshop={w}
              selected={selectedId === w.id}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
